// One driver: the cash deposits on Fleetly's account whose typed name could be
// his, and a place to put the paper he is missing.
//
// WHY THIS SCREEN EXISTS AT ALL. cash_transactions holds its first declared
// deposit on 8 July 2026. Before that, for seventeen months, men banked cash and
// nothing in this system was ever going to ask them to say so. 848 ATM deposits
// totalling R250,430 sit in the loaded statements for those months, every one of
// them with a name typed at the machine, and until 19 September 2026 not one of
// them could be connected to anybody.
//
// A CREDIT CARRYING HIS NAME IS NOT A MATCH AND THIS SCREEN NEVER CALLS IT ONE.
// A match says the money he declared arrived. An attribution says only that money
// arrived with his name on it. The second is weaker and it is still evidence, and
// in these months it is the only evidence there is. The label on the button says
// what it asserts, in those words, rather than "confirm".
//
// AN AMBIGUOUS CREDIT COSTS A SENTENCE. On 11 August 2026 this account took
// deposits under "Steven", "Steven Booi" and "Ntokozo" within minutes of each
// other, one with no surname. The machine will not touch those, and a person
// still can, because a Driver Servant who knows which of the two banked that day
// knows something the database does not. What he may not do is assign it on one
// click the way an unambiguous one is assigned: the button opens a box and the
// reason he gives goes on the record beside his name.
//
// So the gradient is: the machine writes only where there is one answer, a person
// writes anywhere and says why when there is more than one answer. Removing the
// button entirely would read as caution and would actually just push the decision
// off this screen and into a conversation nobody can read afterwards.
import { useState } from 'react'
import { AlertCircle, Banknote, ShieldQuestion, Undo2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Chip } from '../interviews/Chip'
import { zar } from './cashModel'

export type Credit = Record<string, any>

export function DriverCreditPanel({
  driverId,
  driverName,
  credits,
  loading,
  mayAttribute,
  busy,
  onAttribute,
  onWithdraw,
}: {
  driverId: string
  driverName: string
  credits: Credit[]
  loading: boolean
  mayAttribute: boolean
  busy: boolean
  onAttribute: (args: {
    bank_statement_line_id: string
    amount_zar: number
    // Required where the name answers to more than one driver, and absent where
    // there was only ever one man it could be.
    reason?: string
  }) => Promise<void>
  onWithdraw: (args: { attribution_id: string; reason: string }) => Promise<void>
}) {
  const [withdrawing, setWithdrawing] = useState<string | null>(null)
  const [assigning, setAssigning] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [why, setWhy] = useState('')

  const mine = credits.filter((c) => String(c.driver_id) === String(driverId))
  const held = mine.filter((c) => c.attribution_id && String(c.attributed_to) === String(driverId))
  const open = mine.filter((c) => !c.attribution_id)

  if (loading) {
    return <p className="text-xs text-muted-foreground">Looking through the statements…</p>
  }

  if (mine.length === 0) {
    return (
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldQuestion className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        No cash deposit in any loaded statement carries a name matching {driverName}. That is a
        statement about what has been loaded and what was typed at the machine, and not about what
        he did.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {held.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-foreground">
            Recorded as his: {held.length} deposit{held.length === 1 ? '' : 's'},{' '}
            {zar(held.reduce((t, c) => t + Number(c.amount_zar ?? 0), 0))}
          </p>
          <ul className="divide-y divide-border rounded-md border border-border">
            {held.map((c) => (
              <li key={String(c.bank_statement_line_id)} className="p-2">
                <div className="flex flex-wrap items-baseline gap-2 text-xs">
                  <span className="tabular-nums text-muted-foreground">{c.txn_date}</span>
                  <span className="font-medium tabular-nums">{zar(c.amount_zar)}</span>
                  <span className="text-muted-foreground">{c.reference || c.description}</span>
                  {mayAttribute && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-6"
                      disabled={busy}
                      onClick={() => {
                        setWithdrawing(
                          withdrawing === String(c.attribution_id) ? null : String(c.attribution_id),
                        )
                        setReason('')
                      }}
                    >
                      <Undo2 className="mr-1 h-3 w-3" /> Withdraw
                    </Button>
                  )}
                </div>
                {withdrawing === String(c.attribution_id) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Input
                      className="h-7 flex-1 text-xs"
                      placeholder="Why this is not his money"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-7"
                      disabled={busy || reason.trim().length === 0}
                      onClick={async () => {
                        await onWithdraw({
                          attribution_id: String(c.attribution_id),
                          reason: reason.trim(),
                        })
                        setWithdrawing(null)
                        setReason('')
                      }}
                    >
                      Withdraw it
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {open.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-foreground">
            Carrying a name like his, not recorded as his: {open.length}
          </p>
          <ul className="divide-y divide-border rounded-md border border-border">
            {open.map((c) => {
              const ambiguous = Number(c.match_count ?? 1) > 1
              return (
                <li key={String(c.bank_statement_line_id)} className="p-2">
                  <div className="flex flex-wrap items-baseline gap-2 text-xs">
                    <span className="tabular-nums text-muted-foreground">{c.txn_date}</span>
                    <span className="font-medium tabular-nums">{zar(c.amount_zar)}</span>
                    <span className="text-muted-foreground">{c.reference || c.description}</span>
                    {ambiguous && (
                      <Chip tone="amber">answers to {c.match_count} drivers</Chip>
                    )}
                    {!c.worked_that_month && !ambiguous && (
                      <Chip tone="amber">no shift that month</Chip>
                    )}
                    {mayAttribute && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="ml-auto h-6"
                        disabled={busy}
                        onClick={() => {
                          if (!ambiguous) {
                            void onAttribute({
                              bank_statement_line_id: String(c.bank_statement_line_id),
                              amount_zar: Number(c.amount_zar),
                            })
                            return
                          }
                          setAssigning(
                            assigning === String(c.bank_statement_line_id)
                              ? null
                              : String(c.bank_statement_line_id),
                          )
                          setWhy('')
                        }}
                      >
                        <Banknote className="mr-1 h-3 w-3" />
                        {ambiguous ? 'Assign it to him' : 'This money is his'}
                      </Button>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{c.note}</p>
                  {assigning === String(c.bank_statement_line_id) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Input
                        className="h-7 flex-1 text-xs"
                        placeholder={`How you know this is ${driverName.split(' ')[0]} and not the other driver`}
                        value={why}
                        onChange={(e) => setWhy(e.target.value)}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="h-7"
                        disabled={busy || why.trim().length === 0}
                        onClick={async () => {
                          await onAttribute({
                            bank_statement_line_id: String(c.bank_statement_line_id),
                            amount_zar: Number(c.amount_zar),
                            reason: why.trim(),
                          })
                          setAssigning(null)
                          setWhy('')
                        }}
                      >
                        Record it
                      </Button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
        Recording a deposit as his says the money arrived with his name typed on it. It does not say
        he declared it, it is weaker evidence than a declaration the bank confirms, and it changes no
        salary.
      </p>
    </div>
  )
}
